import { createTrackEvent as track } from 'utils/segment';
import {
  COPY_CRITERIA_PROPERTY,
  COPY_SEARCH_QUERY,
  TOGGLE_EDIT_MODE,
  REDO_QUERY,
  UNDO_QUERY,
} from 'modules/queryBuilder/legacyQueryBuilder/actionTypes';
import * as queryBuilderSelectors from 'modules/queryBuilder/legacyQueryBuilder/selectors';
import {
  CATALOG_COLLECTION_NAME,
  COLLECTION_TYPE_DYNAMIC,
  COLLECTION_TYPE_STATIC,
} from 'modules/catalog/catalogConstants';
import * as t from './actionTypes';
import * as s from './selectors';

/*------------------------------------------------------------------------------------------------*/
/* State Getters                                                                                  */
/*------------------------------------------------------------------------------------------------*/
/**
 * Gets the collection searchQuery data from state.
 * @param { Object } state: The application state.
 * @returns { Object } The collection containing the combinator and searchQueries.
 */
export const getCollection = (state) => {
  const combinator = queryBuilderSelectors.selectCombinator(state);
  const searchQueries = queryBuilderSelectors.selectSearchGroups(state);
  return { combinator, searchQueries };
};

/**
 * Converts boolean to the collection type.
 * @param { Boolean } isDynamic: If the collection is dynamic.
 * @returns { String } The type of the collection.
 */
export const getCollectionType = (isDynamic) =>
  isDynamic ? COLLECTION_TYPE_DYNAMIC : COLLECTION_TYPE_STATIC;

/**
 * Gets the the basic information of the collection from state.
 * @param { Object } state: The application state.
 * @returns { Object } The collection information containing the associated catalog, id, name and type.
 */
export const getCollectionInfo = (state) => {
  const associatedCatalog = s.selectCatalog(state);
  const isDynamic = queryBuilderSelectors.selectHasDynamicFields(state);
  const collectionType = getCollectionType(isDynamic);
  const id = s.selectId(state);
  const name = s.selectName(state);
  return {
    associated_catalog: associatedCatalog,
    collection_id: id,
    collection_name: name,
    collection_type: collectionType,
  };
};

/**
 * If the collection is using the order by field.
 * @param { Object } state: The application state.
 * @returns { Boolean } Order by field is being used.
 */
export const getIsOrderByUsed = (state) => {
  const orderBy = queryBuilderSelectors.selectOrderBy(state);
  return orderBy.field !== undefined;
};

/**
 * Gets the order by and limit values for the collection.
 * @param { Object } state: The application state.
 * @returns { Boolean } Order by and limit values.
 */
export const getOrderAndLimit = (state) => {
  const itemsToShow = queryBuilderSelectors.selectSizeLimit(state);
  const orderByUsed = getIsOrderByUsed(state);
  return { items_to_show: itemsToShow, order_by_used: orderByUsed };
};

/*------------------------------------------------------------------------------------------------*/
/* Event Formatters                                                                               */
/*------------------------------------------------------------------------------------------------*/
export const formatEvent = (e, state) => getCollectionInfo(state);

export const formatSavedEvent = (e, state) => {
  const collectionInfo = getCollectionInfo(state);
  const collection = getCollection(state);
  const orderAndLimit = getOrderAndLimit(state);
  return { ...collectionInfo, collection, ...orderAndLimit };
};

export const formatCopySingleEvent = (e, state, action) => {
  const { isDynamic } = action;
  const collectionInfo = getCollectionInfo(state);
  const criteriaType = getCollectionType(isDynamic);
  return { ...collectionInfo, criteria_type: criteriaType, copy_type: 'single' };
};

export const formatCopyGroupEvent = (e, state) => {
  const collectionInfo = getCollectionInfo(state);
  return { ...collectionInfo, copy_type: 'group' };
};

export const formatEditEvent = (e, state) => {
  const collectionInfo = getCollectionInfo(state);
  const collection = getCollection(state);
  return { ...collectionInfo, collection };
};

export const shouldTrackEvent = (e, state) => {
  const type = queryBuilderSelectors.selectType(state);
  return type === CATALOG_COLLECTION_NAME;
};

export default {
  [t.CREATE_COLLECTION]: track('Saved Catalog Collection', [], formatSavedEvent),
  [t.UPDATE_COLLECTION]: track('Saved Catalog Collection', [], formatSavedEvent),
  [t.PREVIEW_COLLECTION]: track('Previewed Catalog Collection', [], formatEvent),
  [COPY_CRITERIA_PROPERTY]: track(
    'Copied Catalog Collection Search Criteria',
    [],
    formatCopySingleEvent,
    { shouldTrackEvent },
  ),
  [COPY_SEARCH_QUERY]: track(
    'Copied Catalog Collection Search Criteria',
    [],
    formatCopyGroupEvent,
    { shouldTrackEvent },
  ),
  [TOGGLE_EDIT_MODE]: track('Edit Catalog Collection', [], formatEditEvent, { shouldTrackEvent }),
  [REDO_QUERY]: track('Redo Catalog Collection', [], formatEvent, { shouldTrackEvent }),
  [UNDO_QUERY]: track('Undo Catalog Collection', [], formatEvent, { shouldTrackEvent }),
};
